import { memo, useCallback, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import moment from 'moment';

import { useStyles } from './CalendarRangePicker.styles';
import { CalendarRangePickerProps } from './CalendarRangePicker.types';
import { CalendarRangePickerHeader } from './header/Header';
import { CalendarRangePickerYearHeader } from './header/YearHeader';

export const CalendarRangePicker = memo(
  ({
    startDate,
    endDate,
    minDate,
    maxDate,
    isRange,
    maxDuration,
    showMonthYearPicker,
    showFullMonthYearPicker,
    headerProps,
    onChange,
  }: CalendarRangePickerProps) => {
    const styles = useStyles();

    const handleChange = useCallback(
      (date: Date | [Date, Date] | null) => {
        if (Array.isArray(date)) {
          const dateYear = new Date(date[0]).getFullYear();
          const currentYear = new Date().getFullYear();

          if (dateYear === currentYear && showMonthYearPicker) {
            onChange([new Date(), new Date()]);
          } else {
            onChange(date);
          }

          return;
        }

        if (date) {
          onChange([date]);

          return;
        }

        onChange([]);
      },
      [showMonthYearPicker, onChange],
    );

    const maxDateValue = useMemo(() => {
      if (isRange && maxDuration && startDate && !endDate) {
        return moment(startDate)
          .clone()
          .add(maxDuration - 1, 'days')
          .toDate();
      }

      return maxDate ? maxDate : undefined;
    }, [isRange, maxDate, maxDuration, startDate, endDate]);

    return (
      <DatePicker
        minDate={minDate}
        selected={!isRange ? startDate : undefined}
        startDate={isRange ? startDate : undefined}
        endDate={isRange ? endDate : undefined}
        selectsRange={isRange}
        inline
        disabledKeyboardNavigation
        renderCustomHeader={props =>
          showMonthYearPicker ? (
            <CalendarRangePickerYearHeader {...headerProps} {...props} />
          ) : (
            <CalendarRangePickerHeader {...headerProps} {...props} />
          )
        }
        useWeekdaysShort={true}
        maxDate={maxDateValue}
        showMonthYearPicker={showMonthYearPicker}
        showFullMonthYearPicker={showFullMonthYearPicker}
        calendarClassName={styles.datePicker}
        onChange={handleChange}
      />
    );
  },
);
